import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Heart } from 'lucide-react';
import PropertyCard from './PropertyCard';
import PropertyDetailsModal from './PropertyDetailsModal';
import ScrollReveal from './ScrollReveal';
import { propertiesData } from '../data/propertiesData'; // Datos mock como respaldo
import { api, formatImageUrl } from '../services/api'; // Importamos el servicio API
import { getFavorites, onFavoritesChanged } from '../utils/favorites';

const Properties = () => {
  const { id: propertyIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(() => getFavorites());

  useEffect(() => onFavoritesChanged(() => setFavoriteIds(getFavorites())), []);

  const filters = [
    { id: 'all', label: 'Todas' },
    { id: 'destacado', label: 'Destacados', icon: Star },
    { id: 'terreno', label: 'Terrenos / Lotes' },
    { id: 'casa', label: 'Casas' },
    { id: 'departamento', label: 'Departamentos' },
    { id: 'alquiler', label: 'Alquileres' },
    { id: 'favoritos', label: 'Me gusta', icon: Heart }
  ];

  // Cargar propiedades al montar el componente
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Intentar obtener propiedades desde la API
      const data = await api.getProperties();
      
      // Formatear URLs de imágenes si es necesario
      const formattedData = data.map(property => ({
        ...property,
        image: formatImageUrl(property.image || (property.images && property.images[0])),
        images: property.images ? property.images.map(img => formatImageUrl(img)) : []
      }));
      
      setProperties(formattedData);
      
    } catch (error) {
      console.error('Error cargando propiedades:', error);
      setError('No se pudieron cargar las propiedades. Mostrando datos de ejemplo.');
      
      // Usar datos mock como fallback con URLs formateadas
      const formattedMockData = propertiesData.map(property => ({
        ...property,
        image: formatImageUrl(property.image),
        images: property.images ? property.images.map(img => formatImageUrl(img)) : []
      }));
      
      setProperties(formattedMockData);
      
    } finally {
      setLoading(false);
    }
  };

  // Filtrar propiedades
  const filteredProperties = activeFilter === 'all'
    ? properties
    : activeFilter === 'alquiler'
    ? properties.filter(prop => prop.type === 'alquiler')
    : activeFilter === 'destacado'
    ? properties.filter(prop => prop.featured)
    : activeFilter === 'favoritos'
    ? properties.filter(prop => favoriteIds.includes(prop.id))
    : properties.filter(prop => prop.category === activeFilter);

  // Si se entra directo a /propiedad/:id (por ejemplo, desde un link compartido), abrir esa propiedad
  useEffect(() => {
    if (propertyIdFromUrl && properties.length > 0) {
      const property = properties.find(p => String(p.id) === propertyIdFromUrl);
      if (property) {
        setSelectedProperty(property);
        setIsModalOpen(true);
      }
    }
  }, [propertyIdFromUrl, properties]);

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
    navigate(`/propiedad/${property.id}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
    navigate('/');
  };

  // Componente de carga
  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center py-24">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-8 h-8 border-2 border-transparent border-t-primary/60 rounded-full animate-spin"></div>
        </div>
      </div>
      <p className="mt-6 text-lg text-gray-600">Cargando propiedades...</p>
      <p className="text-sm text-gray-500 mt-2">Esto puede tomar unos segundos</p>
    </div>
  );

  // Componente de error
  const ErrorMessage = () => (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-r-lg">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-medium text-yellow-800">
            {error}
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              El sistema está utilizando datos de ejemplo. Esto es normal si:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>El servidor de propiedades está iniciándose</li>
              <li>Es la primera vez que accedes al sitio</li>
              <li>Hay problemas temporales de conexión</li>
            </ul>
            <button
              onClick={loadProperties}
              className="mt-4 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section id="propiedades" className="py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Propiedades y Terrenos Disponibles
            </h2>
            <p className="text-muted-foreground text-lg">
              Selección cuidada de terrenos, casas, departamentos y opciones de alquiler en las mejores ubicaciones.
            </p>
          </ScrollReveal>

          {/* Mostrar error si existe */}
          {error && <ErrorMessage />}

          {/* Filtros - solo mostrar si no está cargando */}
          {!loading && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {filters.map(filter => {
                const Icon = filter.icon;
                const isFavoritosFilter = filter.id === 'favoritos';
                const isActive = activeFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                      isActive
                        ? isFavoritosFilter
                          ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                          : 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                        : 'bg-white text-muted-foreground hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {Icon && (
                      <Icon
                        className={`w-4 h-4 ${
                          isActive && isFavoritosFilter ? 'fill-white' : ''
                        } ${isActive && filter.id === 'destacado' ? 'fill-current' : ''}`}
                      />
                    )}
                    {filter.label}
                    {isFavoritosFilter && favoriteIds.length > 0 && (
                      <span className={`ml-0.5 text-xs rounded-full px-1.5 py-0.5 ${
                        isActive ? 'bg-white/25' : 'bg-red-100 text-red-600'
                      }`}>
                        {favoriteIds.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Mostrar spinner de carga */}
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Grid de propiedades */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProperties.map((property, index) => (
                  <ScrollReveal key={property.id} delay={(index % 6) * 80}>
                    <PropertyCard
                      property={property}
                      onViewDetails={handleViewDetails}
                    />
                  </ScrollReveal>
                ))}
              </div>

              {/* Mensaje si no hay propiedades */}
              {filteredProperties.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-6">
                    {activeFilter === 'favoritos' ? (
                      <Heart className="w-10 h-10 text-gray-400" />
                    ) : (
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {activeFilter === 'favoritos' ? 'Todavía no marcaste propiedades' : 'No hay propiedades en esta categoría'}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {activeFilter === 'favoritos'
                      ? 'Tocá el corazón de las propiedades que te interesen para guardarlas acá.'
                      : 'Prueba seleccionando otra categoría o vuelve a intentarlo más tarde.'}
                  </p>
                  <button
                    onClick={() => setActiveFilter('all')}
                    className="mt-6 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Ver todas las propiedades
                  </button>
                </div>
              )}

              {/* Contador de propiedades */}
              {filteredProperties.length > 0 && (
                <div className="mt-12 text-center">
                  <p className="text-gray-600">
                    Mostrando {filteredProperties.length} de {properties.length} propiedades
                  </p>
                  {activeFilter !== 'all' && (
                    <button
                      onClick={() => setActiveFilter('all')}
                      className="mt-4 px-4 py-2 text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      Ver todas las propiedades →
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Botón para recargar en caso de error */}
          {error && !loading && (
            <div className="text-center mt-8">
              <button
                onClick={loadProperties}
                className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Actualizar propiedades
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Modal de detalles */}
      <PropertyDetailsModal 
        property={selectedProperty}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Properties;